import { Disclosure } from "@headlessui/react";
import { BriefcaseIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import CloseIconButton from "../../../../components/CloseIconButton";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import { jobShowState } from "../atoms/atoms";
import ReportDetailsTable from "./ReportDetailsTable";
import EmailLogDetailsTable from "./EmailLogDetailsTable";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../PermissionsConstants";
import AddButton from "../../../../components/AddButton";
import useGetJobDetailsById from "../hooks/useGetJobDetailsById";
import ExecutionDetailsTable from "./ExecutionDetailsTable";

interface JobDetailsProps {
  rowData: any;
  handleEdit: any;
  setActionAddState;
}

export const JobViewDetails: React.FC<JobDetailsProps> = ({
  rowData,
  handleEdit,
  setActionAddState,
}) => {
  const {
    data: details,
    isFetching,
    isSuccess,
  } = useGetJobDetailsById(rowData?.jobConfigId ?? 0);
  const [jobDetails, setJobDetails] = useState<any>([]);
  const [, setShowState] = useRecoilState(jobShowState);
  const editPermission = PERMISSIONS.permission_utilities_jobconfiguration_edit;
  const addPermission = PERMISSIONS.permission_utilities_jobconfiguration_add;
  const userPermissions = useRecoilValue(permissionsSelector);

  useEffect(() => {
    if (isSuccess && details) {
      setJobDetails(details);
    }
  }, [isSuccess, details]);
  return (
    <>
      {!isFetching && details && jobDetails && (
        <>
          <div className="flex mx-2 justify-between pb border-secondary border-b-2">
            <div className="flex gap-2 items-center pb-2 ">
              <div>
                <BriefcaseIcon className="text-white bg-blue-500 rounded-md h-8 w-8 p-1" />
              </div>
              <div className="leading-tight">
                <h3 className="text-xs font-medium text-gray-500">
                  Utilities Module
                </h3>
                <h1 className="mr-auto font-bold text-primary text-md uppercase">
                  {jobDetails?.eventName}
                </h1>
              </div>
            </div>
            <div className="flex justify-between">
              <div className="flex items-center gap-2">
                {userPermissions.includes(addPermission) && (
                  <AddButton
                    buttonText="Add"
                    onClick={() => setActionAddState(true)}
                  />
                )}
                {userPermissions.includes(editPermission) && (
                  <EditOutlineButton
                    buttonText="Edit"
                    onClick={() => {
                      handleEdit(jobDetails);
                    }}
                  />
                )}
              </div>
              <div className="">
                <CloseIconButton
                  onClick={() => {
                    setShowState(false);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="m-2 bg-white box-shadow rounded-lg h-full">
            <div className="text-quinary font-bold font-sans py-2 px-4">
              Event Details
              <div className="grid grid-cols-1 md:grid-cols-2 text-xs p-2">
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Event Name:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.eventName}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Subject:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.subject}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Schedule Days:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.scheduleDays}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Is Active:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.isActive ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Schedule Time:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {" "}
                      {jobDetails?.scheduleTime}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Server Instance:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.serverInstance}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Recurring:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.recurring}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      With Email:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.withEmail ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Time Value:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.timeValue}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Day of Month Value:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.monthDayValue}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Guid:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.guid}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Cron Expression:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.cronExpression}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="m-2 bg-white box-shadow rounded-lg h-full pb-2">
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRightIcon
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5 `}
                    />
                    <div className="disclosureLabel ml-2">Report Details</div>
                  </Disclosure.Button>

                  <Disclosure.Panel className="text-xs">
                    <ReportDetailsTable data={jobDetails} />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
          <div className="m-2 mb-2 mx-2 bg-white rounded-lg box-shadow">
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRightIcon
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5 `}
                    />
                    <div className="disclosureLabel ml-2">
                      Email Log Details
                    </div>
                  </Disclosure.Button>

                  <Disclosure.Panel className="text-xs">
                    <EmailLogDetailsTable data={jobDetails} />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
          <div className="m-2 mb-2 mx-2 bg-white rounded-lg box-shadow">
            <Disclosure defaultOpen>
              {({ open }) => (
                <>
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRightIcon
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5 `}
                    />
                    <div className="disclosureLabel ml-2">
                      Execution Details
                    </div>
                  </Disclosure.Button>

                  <Disclosure.Panel className="text-xs">
                    <ExecutionDetailsTable
                      jobConfigId={jobDetails.jobConfigId}
                    />
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          </div>
          <div className="m-2 bg-white box-shadow rounded-lg h-full">
            <div className="text-quinary font-bold font-sans py-2 px-4">
              System Information
              <div className="grid grid-cols-1 md:grid-cols-2 pt-2">
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Created By:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {" "}
                      {jobDetails?.createdBy}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Modified By:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.modifiedBy}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Created Date:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.createdDate
                        ? new Date(
                            jobDetails?.createdDate
                          )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                </div>
                <div className="w-full border-b border-gray-200 py-1">
                  <div className="grid grid-cols-2">
                    <label className="expandedLabel fontExpand text-black text-left ml-2">
                      Modified Date:
                    </label>
                    <div className="col-span-1 text-black expandedData fontExpand ">
                      {jobDetails?.modifiedDate
                        ? new Date(
                            jobDetails?.modifiedDate
                          )?.toLocaleDateString()
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default JobViewDetails;
